import {
  AuthSocialResponse,
  AuthResponse,
  AuthStateModel,
  IErrorResponse,
  ISuccessResponse,
} from './auth-state.model';
import { Action, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import {
  AuthFacebook,
  AuthGoogle,
  CreateUser,
  FacebookAuthorize,
  GoogleAuthorize,
  SendOtpToEmail,
  VerifyOtp,
} from './auth.actions';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { NotificationService } from '../../services/notification.service';
import { Me } from '../user/user.actions';

@State<AuthStateModel>({
  name: 'auth',
})
@Injectable()
export class AuthState {
  constructor(
    private auth: AuthService,
    private notification: NotificationService
  ) {}

  /* ------------------------------- OTP ------------------------------- */

  @Action(SendOtpToEmail)
  sendOtpToEmail(
    { dispatch, patchState }: StateContext<ISuccessResponse | IErrorResponse>,
    { email, domain }: SendOtpToEmail
  ): Observable<ISuccessResponse | IErrorResponse> {
    patchState({ status: undefined });

    return this.auth.sendOtp(email).pipe(
      tap((res) => {
        if (res.status) {
          patchState({
            status: res.status,
          });

          this.notification.success('OTP sent to your email');
        }
      }),
      catchError((err) => {
        if (err.error.error === 'User with this email not found') {
          dispatch(new CreateUser(email, domain));
        } else {
          this.notification.error(`${err.error.error}`);
        }

        return of(err);
      })
    );
  }

  @Action(VerifyOtp)
  verifyOtp(
    { patchState, dispatch }: StateContext<AuthResponse>,
    { email, otpCode }: VerifyOtp
  ): Observable<AuthResponse> {
    patchState({ status: undefined });

    return this.auth.verifyOtp(email, otpCode).pipe(
      tap((res) => {
        if (res.status) {
          patchState({
            status: res.status,
            user: res.user,
          });

          localStorage.setItem('ngStorage-authData', JSON.stringify(res.user));
          dispatch(new Me());
        }
      }),
      catchError((err) => {
        this.notification.error('Invalid OTP, please try again');
        return of(err);
      })
    );
  }

  /* ------------------------------- User ------------------------------- */

  @Action(CreateUser)
  createUser(
    { dispatch }: StateContext<AuthStateModel>,
    { email, domain }: CreateUser
  ): Observable<any> {
    return this.auth.getPortalId(domain).pipe(
      mergeMap((res) => {
        const portalId = res?.portal?.portal_id || '';
        return this.auth.registerUser(email, portalId).pipe();
      }),
      tap((res) => {
        if (res.status) {
          this.notification.success('User registered successfully');
          dispatch(new SendOtpToEmail(email, domain));
        }
      }),
      catchError((err) => {
        this.notification.error(`${err.error.error}`);
        return of(err);
      })
    );
  }

  /* ------------------------------- Google ------------------------------- */

  @Action(AuthGoogle)
  authGoogle(
    { patchState }: StateContext<AuthSocialResponse>,
    {}: AuthGoogle
  ): Observable<AuthSocialResponse> {
    return this.auth.googleAuthInit().pipe(
      tap((res) => {
        if (res.status) {
          patchState({
            data: res.data,
          });
        }
      }),
      catchError((err) => {
        this.notification.error('Google authentication failed');
        return of(err);
      })
    );
  }

  @Action(GoogleAuthorize)
  googleAuthorize(
    { patchState, dispatch }: StateContext<AuthResponse>,
    { code }: GoogleAuthorize
  ): Observable<AuthResponse> {
    patchState({ status: undefined });

    return this.auth.googleAuthorize(code).pipe(
      tap((res) => {
        if (res.status) {
          patchState({
            status: res.status,
            user: res.user,
          });

          localStorage.setItem('ngStorage-authData', JSON.stringify(res.user));
          dispatch(new Me());
        }
      }),
      catchError((err) => {
        this.notification.error('Error during Google login');
        return of(err);
      })
    );
  }

  /* ------------------------------- Facebook ------------------------------- */

  @Action(AuthFacebook)
  authFacebook(
    { patchState }: StateContext<AuthSocialResponse>,
    {}: AuthFacebook
  ): Observable<AuthSocialResponse> {
    return this.auth.facebookAuthInit().pipe(
      tap((res) => {
        if (res.status) {
          patchState({
            data: res.data,
          });
        }
      }),
      catchError((err) => {
        this.notification.error('Authentication failed');
        return of(err);
      })
    );
  }

  @Action(FacebookAuthorize)
  facebookAuthorize(
    { patchState, dispatch }: StateContext<AuthResponse>,
    { code }: FacebookAuthorize
  ): Observable<AuthResponse> {
    patchState({ status: undefined });

    return this.auth.facebookAuthorize(code).pipe(
      tap((res) => {
        if (res.status) {
          patchState({
            status: res.status,
            user: res.user,
          });

          localStorage.setItem('ngStorage-authData', JSON.stringify(res.user));
          dispatch(new Me());
        }
      }),
      catchError((err) => {
        this.notification.error('Not authorized');
        return of(err);
      })
    );
  }
}
